/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DialogTitle, DialogDescription } from '@/shared/ui/dialog';
import { Photo, Photo360Response, PhotoMarker } from './types/types';
import FABMenu from './fab_menu/FABMenu';
import HotspotLegend from './HotspotLegend';
import PhotoNavigation from './PhotoNavigation';
import HotspotObjectsDialog from './hot-spots/HotspotObjectsDialog';
import { useSyncPannellumHotspots } from './hooks/useSyncPannellumHotspots';

import 'pannellum/build/pannellum.css';
import 'pannellum/build/pannellum.js';
import './styles/HotspotStyles.css';

import { PITCH_OFFSET } from './constants/constants';

// Type definitions for hotspot system
interface HotspotObject {
  id: number | string;
  type: 'note' | 'image' | 'label';
  title: string;
  created_at: string;
  updated_at: string;
}

interface Hotspot {
  id: number;
  pitch: number;
  yaw: number;
  hfov: number;
  overlay_count: number;
  created_by: string | null;
  title?: string;
  objects: HotspotObject[];
  photo_id: number;
  created_at: string;
  updated_at: string;
}

interface LegacyHotspot {
  id: number;
  pitch: number;
  yaw: number;
  title?: string;
}

interface HotspotsData {
  hotspots: (Hotspot | LegacyHotspot)[];
}

// Photo data for navigation (from PanoramaViewerSimple)
interface Photo360Data {
  image_url?: string;
  photo_id: number;
  photo_name: string;
  date: string;
  prev_photo?: number | null;
  next_photo?: number | null;
  prev_date?: string | null;
  next_date?: string | null;
  prev_date_photo_id?: number | null;
  next_date_photo_id?: number | null;
}

// Declare Pannellum for TypeScript (keep as any to match existing declaration)
declare global {
  interface Window {
    pannellum: any;
  }
}

/**
 * Unified PanoramaViewer Component
 * 
 * A configurable 360° panorama viewer with optional features:
 * - FAB menu for adding hotspots (enableFAB)
 * - Hotspot display on panorama (enableHotspots)
 * - Photo navigation prev/next (enablePhotoNav)
 * - Date navigation prev/next (enableDateNav)
 * - Marker navigation (enableMarkerNav)
 * - Hotspot legend sidebar (enableLegend)
 * 
 * All features are disabled by default for minimal mode.
 */
interface PanoramaViewerProps {
  // Required props
  photoId: number;
  imageUrl: string;
  
  // Optional feature flags (all default to false)
  enableFAB?: boolean;
  enableHotspots?: boolean;
  enablePhotoNav?: boolean;
  enableDateNav?: boolean;
  enableMarkerNav?: boolean;
  enableLegend?: boolean;
  
  // Data props (only needed if corresponding feature is enabled)
  photoData?: Photo360Response | Photo360Data | null;
  hotspotsData?: HotspotsData | null;
  markers?: PhotoMarker[];
  currentMarkerPhotos?: Photo[];
  currentPhotoIndex?: number;
  projectId?: string;
  
  // Callbacks
  onNavigateToPhotoId?: (photoId: number) => void;
  onNavigatePhoto?: (direction: 'prev' | 'next') => void;
  onHotspotCreated?: () => Promise<void>;
  onCloseDialogs?: () => void;
  
  // Navigation state (for legacy compatibility)
  hasPhotoPrev?: boolean;
  hasPhotoNext?: boolean;
  
  // Target navigation (for navigating to specific hotspot)
  targetCoordinates?: { pitch: number; yaw: number } | null;
  onTargetReached?: () => void;

  // Share feature - layout context for location preview
  markerNumber?: number;
  layoutImageUrl?: string;
  layoutImageDimensions?: { width: number; height: number };
  photoMarkers?: PhotoMarker[];
}

const PanoramaViewer: React.FC<PanoramaViewerProps> = ({
  // Required
  photoId,
  imageUrl,
  
  // Feature flags (all default to false)
  enableFAB = false,
  enableHotspots = false,
  enablePhotoNav = false,
  enableDateNav = false,
  enableMarkerNav = false,
  enableLegend = false,
  
  // Data props
  photoData,
  hotspotsData,
  markers = [],
  currentMarkerPhotos = [],
  currentPhotoIndex = 0,
  projectId,
  
  // Callbacks
  onNavigateToPhotoId,
  onNavigatePhoto,
  onHotspotCreated,
  
  // Navigation state
  hasPhotoPrev = false,
  hasPhotoNext = false,
  
  // Target navigation
  targetCoordinates,
  onTargetReached,

  // Share feature
  markerNumber,
  layoutImageUrl,
  layoutImageDimensions,
  photoMarkers,
}) => {
  // Extract photo navigation data (supports both Photo360Response and Photo360Data)
  const photoName = (photoData as any)?.photo_name || `Photo ${photoId}`;
  const photoDate = (photoData as any)?.date || '';
  const prevPhotoId = (photoData as any)?.prev_photo;
  const nextPhotoId = (photoData as any)?.next_photo;
  const prevDatePhotoId = (photoData as any)?.prev_date_photo_id;
  const nextDatePhotoId = (photoData as any)?.next_date_photo_id;
  const hasPrevPhoto = !!prevPhotoId;
  const hasNextPhoto = !!nextPhotoId;
  const hasPrevDate = !!(photoData as any)?.prev_date;
  const hasNextDate = !!(photoData as any)?.next_date;

  // Panorama-specific state
  const [panoramaHotspots, setPanoramaHotspots] = useState<
    Array<{
      id: string;
      type: string;
      pitch: number;
      yaw: number;
      originalId: number;
      label?: string;
    }>
  >([]);

  // New hotspot objects dialog state
  const [showHotspotObjects, setShowHotspotObjects] = useState(false);
  const [currentHotspot, setCurrentHotspot] = useState<Hotspot | null>(null);
  const [viewerLoaded, setViewerLoaded] = useState(false);

  // Refs
  const pannellumViewerRef = useRef<any>(null);
  const initializedPhotoIdRef = useRef<number | null>(null);
  const gyroWasActiveRef = useRef(false);

  // Function to handle hotspot clicks - show objects dialog
  // Defined early so it can be used by useSyncPannellumHotspots
  const handleHotspotClick = useCallback(
    (rawHotspot: any) => {
      // Convert to proper format that dialog expects
      const hotspot: Hotspot = {
        id: rawHotspot.id,
        pitch: rawHotspot.pitch,
        yaw: rawHotspot.yaw,
        hfov: rawHotspot.hfov || 100,
        overlay_count: rawHotspot.overlay_count || 0,
        created_by: rawHotspot.created_by || null,
        title: rawHotspot.title,
        photo_id: rawHotspot.photo_id || photoId,
        created_at: rawHotspot.created_at || new Date().toISOString(),
        updated_at: rawHotspot.updated_at || new Date().toISOString(),
        objects: rawHotspot.objects || [],
      };

      setCurrentHotspot(hotspot);
      setShowHotspotObjects(true);
    },
    [photoId]
  );

  // Sync Pannellum hotspots with React Query data
  // This bridges React's declarative state with Pannellum's imperative API
  useSyncPannellumHotspots(
    pannellumViewerRef,
    hotspotsData,
    handleHotspotClick,
    enableHotspots && viewerLoaded  // Only sync when viewer is loaded
  );

  // Initialize panorama when we have the required data
  useEffect(() => {
    // Only initialize if we have imageUrl AND we haven't initialized this photo yet
    // If hotspots are enabled, wait for hotspotsData too
    const hotspotsReady = !enableHotspots || !!hotspotsData;
    
    if (imageUrl && photoId && hotspotsReady && photoId !== initializedPhotoIdRef.current) {
      initializedPhotoIdRef.current = photoId;
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        initializePanorama();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, hotspotsData, photoId, enableHotspots]);

  // Cleanup when photo changes or on unmount
  useEffect(() => {
    return () => {
      if (pannellumViewerRef.current) {
        gyroWasActiveRef.current = pannellumViewerRef.current.isOrientationActive();
        pannellumViewerRef.current.destroy();
        pannellumViewerRef.current = null;
        setViewerLoaded(false);
      }
    };
  }, [photoId]);

  // Sync legend data with hotspotsData
  useEffect(() => {
    if (enableHotspots && hotspotsData?.hotspots) {
      const legendData = hotspotsData.hotspots.map((hotspot) => ({
        id: `hotspot-${hotspot.id}`,
        type: 'info',
        pitch: hotspot.pitch,
        yaw: hotspot.yaw,
        originalId: hotspot.id,
        title: 'title' in hotspot ? hotspot.title || 'Hotspot' : 'Hotspot',
      }));
      setPanoramaHotspots(legendData);
    } else {
      setPanoramaHotspots([]);
    }
  }, [hotspotsData, enableHotspots]);

  const initializePanorama = async () => {
    if (!imageUrl || !photoId) {
      return;
    }

    // Use imageUrl directly - no proxy needed
    // The signed GCS URLs work directly with Pannellum
    const finalImageUrl = imageUrl;

    // Check if Pannellum is available
    if (!window.pannellum) {
      console.error('Pannellum not available on window object');
      return;
    }

    // Check DOM element
    const panoramaDiv = document.getElementById('panorama');
    if (!panoramaDiv) {
      console.error('Panorama div not found in DOM');
      return;
    }

    try {
      if (pannellumViewerRef.current) {
        pannellumViewerRef.current.destroy();
        pannellumViewerRef.current = null;
      }

      if (panoramaDiv) {
        panoramaDiv.innerHTML = '';
      }

      const viewer = window.pannellum.viewer('panorama', {
        type: 'equirectangular',
        panorama: finalImageUrl,
        autoLoad: true,
        showControls: true,
        hotSpotDebug: false, // Disable debug indicator
        default: {
          firstScene: 'defaultScene',
          sceneFadeDuration: 1000,
          pitch: 0,
          yaw: 0,
          hfov: 100,
        },
        hotSpots: [],
      });
      
      pannellumViewerRef.current = viewer;

      const cleanup = () => {
        if (pannellumViewerRef.current) {
          pannellumViewerRef.current.destroy();
          pannellumViewerRef.current = null;
        }
        setPanoramaHotspots([]);
      };

      const modalElement = panoramaDiv?.closest('.dialog-content');
      if (modalElement) {
        const existingCleanup = modalElement.getAttribute('data-cleanup');
        if (!existingCleanup) {
          modalElement.setAttribute('data-cleanup', 'true');
          modalElement.addEventListener('close', cleanup);
        }
      }

      viewer.on('load', function () {
        setViewerLoaded(true);
        
        setTimeout(() => {
          if (pannellumViewerRef.current) {
            pannellumViewerRef.current.resize();
          }
        }, 100);

        // Restore gyro if it was active before photo switch
        if (gyroWasActiveRef.current && pannellumViewerRef.current?.isOrientationSupported()) {
          pannellumViewerRef.current.startOrientation();
        }

        if (targetCoordinates) {
          setTimeout(() => {
            if (pannellumViewerRef.current) {
              pannellumViewerRef.current.lookAt(
                targetCoordinates.pitch + PITCH_OFFSET,
                targetCoordinates.yaw,
                undefined,
                1000
              );
              onTargetReached?.();
            }
          }, 100);
        }
      });

      viewer.on('error', function (error: Error) {
        console.error('Pannellum error:', error);
      });

    } catch (error) {
      console.error('Error setting up Pannellum viewer:', error);
    }
  };

  const navigateToHotspot = useCallback((pitch: number, yaw: number) => {
    if (pannellumViewerRef.current) {
      pannellumViewerRef.current.lookAt(pitch + PITCH_OFFSET, yaw, undefined, 750);
    }
  }, []);

  // Handle hotspot click from legend
  const handleHotspotClickFromLegend = useCallback(
    (hotspotInfo: {
      id: string;
      type: string;
      pitch: number;
      yaw: number;
      originalId: number;
      label?: string;
    }) => {
      // Navigate to the hotspot first
      if (pannellumViewerRef.current) {
        pannellumViewerRef.current.lookAt(hotspotInfo.pitch, hotspotInfo.yaw, undefined, 750);
      }

      // Find the hotspot data and show objects dialog
      const rawHotspot = hotspotsData?.hotspots.find((h) => h.id === hotspotInfo.originalId);
      if (rawHotspot) {
        handleHotspotClick(rawHotspot);
      }
    },
    [hotspotsData, handleHotspotClick]
  );

  // Handle object deletion
  const handleObjectDeleted = useCallback(
    async (_objectId: number, _objectType: string) => {
      try {
        // Refresh hotspots after deletion
        if (onHotspotCreated) {
          await onHotspotCreated();
        }
      } catch (error) {
        console.error('Error deleting object:', error);
      }
    },
    [onHotspotCreated]
  );

  // Show loading state if no imageUrl
  if (!imageUrl) {
    return (
      <div className="relative w-full h-full">
        <DialogTitle />
        <DialogDescription />
        <div className="flex items-center justify-center h-full bg-black">
          <div className="text-white text-lg">Loading panorama...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <DialogTitle />
      <DialogDescription />

      {/* Panorama container */}
      <div
        id="panorama"
        className="w-full h-full rounded-lg"
        style={{ backgroundColor: '#000' }}
      />

      {/* FAB Menu - optional */}
      {enableFAB && onHotspotCreated && (
        <FABMenu
          pannellumViewerRef={pannellumViewerRef}
          photoId={photoId}
          onHotspotCreated={onHotspotCreated}
        />
      )}

      {/* Hotspot Legend - optional */}
      {enableLegend && (
        <HotspotLegend
          hotspots={panoramaHotspots}
          onNavigateToHotspot={navigateToHotspot}
          onHotspotClick={handleHotspotClickFromLegend}
          projectId={projectId}
          photoName={photoName}
          photoDate={photoDate}
          currentPhotoIndex={currentPhotoIndex}
          totalPhotos={currentMarkerPhotos.length}
          hasPhotoPrev={hasPhotoPrev}
          hasPhotoNext={hasPhotoNext}
          onNavigatePhoto={onNavigatePhoto}
        />
      )}

      {/* Marker Navigation - optional */}
      {enableMarkerNav && onNavigateToPhotoId && (
        <PhotoNavigation
          markers={markers}
          currentPhotoId={photoId}
          onNavigateToPhoto={onNavigateToPhotoId}
        />
      )}

      {/* Photo/Date Navigation Controls - optional */}
      {(enablePhotoNav || enableDateNav) && (
        <div className="absolute bottom-5 right-5 bg-black/30 p-[15px] rounded-lg shadow-[0_2px_10px_rgba(0,0,0,0.1)] z-[1000]">
          {/* Photo Navigation */}
          {enablePhotoNav && (
            <div className={enableDateNav ? 'mb-[15px]' : ''}>
              <div className="font-bold text-2xl mb-[5px] block text-white">Photo:</div>
              <div className="text-lg mb-2 text-white">{photoName}</div>
              <div className="flex gap-[10px] items-center justify-center">
                <button
                  onClick={() => prevPhotoId && onNavigateToPhotoId?.(prevPhotoId)}
                  disabled={!hasPrevPhoto}
                  className={`bg-[#f6b03e] w-[50px] h-[50px] rounded-full p-[5px] border-none transition-opacity flex items-center justify-center ${
                    hasPrevPhoto ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={() => nextPhotoId && onNavigateToPhotoId?.(nextPhotoId)}
                  disabled={!hasNextPhoto}
                  className={`bg-[#f6b03e] w-[50px] h-[50px] rounded-full p-[5px] border-none transition-opacity flex items-center justify-center ${
                    hasNextPhoto ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
                  }`}
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>
          )}

          {/* Date Navigation */}
          {enableDateNav && (
            <div>
              <div className="font-bold text-2xl mb-[5px] block text-white">Date:</div>
              <div className="text-lg mb-2 text-white">{photoDate}</div>
              <div className="flex gap-[10px] items-center justify-center">
                <button
                  onClick={() => prevDatePhotoId && onNavigateToPhotoId?.(prevDatePhotoId)}
                  disabled={!hasPrevDate}
                  className={`bg-[#000000] w-[50px] h-[50px] rounded-full p-[5px] border-none transition-opacity flex items-center justify-center ${
                    hasPrevDate ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={() => nextDatePhotoId && onNavigateToPhotoId?.(nextDatePhotoId)}
                  disabled={!hasNextDate}
                  className={`bg-[#000000] w-[50px] h-[50px] rounded-full p-[5px] border-none transition-opacity flex items-center justify-center ${
                    hasNextDate ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
                  }`}
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Hotspot Objects Dialog - only when hotspots are enabled */}
      {enableHotspots && (
        <HotspotObjectsDialog
          open={showHotspotObjects}
          hotspot={currentHotspot}
          projectId={projectId}
          onObjectDeleted={handleObjectDeleted}
          onClose={() => {
            setShowHotspotObjects(false);
            setCurrentHotspot(null);
          }}
          markerNumber={markerNumber}
          layoutImageUrl={layoutImageUrl}
          layoutImageDimensions={layoutImageDimensions}
          photoMarkers={photoMarkers}
        />
      )}
    </div>
  );
};

export default PanoramaViewer;
